// -----------------------------------------------------------------------------
// src/components/dashboard/views/CampaignsView.js         2025-06-30
// -----------------------------------------------------------------------------
//
//  • self-fetches with client.users.fetchManagedCampaigns()
//  • accepts   searchQuery, navigate, renderIcon
//  • nothing else removed or re-styled
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useQuery } from 'react-query';

import client from '../../../API';                       // same import style
import { groupByMonth, STATUSES } from './../dashboardUtils';
import { StatBadge, StatRow } from '../dashboardUtils';

/* ──────────────────────────────────────────────────────────────────────────── */
const CampaignsView = ({ navigate, renderIcon, searchQuery = '' }) => {
  /* ───────── fetch campaigns ───────── */
  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery('managedCampaigns', () => client.users.fetchManagedCampaigns(), {
    staleTime: 60_000,
  });

  /* ───────── global search filter ───────── */
  const itemsFilteredBySearch = items.filter((c) =>
    c.campaign_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* ───────── helpers ───────── */
  const fmt = (n) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  /* ───────── sidebar totals ───────── */
  let availableTotal = 0;
  let pendingTotal = 0;
  itemsFilteredBySearch.forEach((c) => {
    const rev = c.campaign_sum ?? 0;
    const cost = c.campaign_cost ?? 0;
    const earn = (rev - cost) * 0.2;
    if (c.paid_date) availableTotal += earn;
    else pendingTotal += earn;
  });

  /* ───────── status filters ───────── */
  const [statusFilters, setStatusFilters] = useState(() =>
    STATUSES.reduce(
      (acc, s) => ({ ...acc, [s]: ['Launched', 'Completed'].includes(s) }),
      {},
    ),
  );
  const resetFilters = () =>
    setStatusFilters(STATUSES.reduce((acc, s) => ({ ...acc, [s]: true }), {}));

  /* ───────── group + filter by month ───────── */
  const rawGrouped = groupByMonth(itemsFilteredBySearch);
  const filteredGrouped = Object.entries(rawGrouped).reduce(
    (acc, [month, camps]) => {
      const visible = camps.filter((c) => statusFilters[c.status]);
      if (visible.length) acc[month] = visible;
      return acc;
    },
    {},
  );

  /* sort month keys so newest appears first. */
  const monthOrder = Object.keys(filteredGrouped).sort(
    (a, b) => new Date(b) - new Date(a),
  );

  /* ───────── refs for smooth-scroll ───────── */
  const panelRefs = useRef({});
  useEffect(() => {
    panelRefs.current = monthOrder.reduce(
      (m, month) => ({ ...m, [month]: panelRefs.current[month] || React.createRef() }),
      {},
    );
  }, [monthOrder]);

  /* ───────── loading / error guards ───────── */
  if (isLoading) return <CircularProgress sx={{ m: 4 }} />;
  if (error)
    return (
      <Alert severity="error" sx={{ m: 4 }}>
        {error.message || 'Failed to load campaigns'}
      </Alert>
    );

  /* ───────── render ───────── */
  return (
    <Box sx={{ display: 'flex', gap: 4 }}>
      {/* ───────── sidebar ───────── */}
      <Paper sx={{ width: 280, p: 2, position: 'sticky', top: 120, borderRadius: 3 }}>
        <Button variant="outlined" fullWidth onClick={resetFilters} sx={{ mb: 2 }}>
          View All
        </Button>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            Available to withdraw: <strong>{fmt(availableTotal)}</strong>
          </Typography>
          <Typography variant="body2">
            Pending to withdraw: <strong>{fmt(pendingTotal)}</strong>
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Months
        </Typography>
        <List dense>
          {monthOrder.map((m) => (
            <ListItem key={m} disablePadding>
              <ListItemButton
                onClick={() =>
                  panelRefs.current[m]?.current?.scrollIntoView({ behavior: 'smooth' })
                }
                sx={{ borderRadius: 2 }}
              >
                <ListItemText primary={m} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Status Filter
        </Typography>
        <List dense>
          {STATUSES.map((s) => (
            <ListItem key={s} disablePadding>
              <ListItemButton
                selected={statusFilters[s]}
                onClick={() =>
                  setStatusFilters((fs) => ({ ...fs, [s]: !fs[s] }))
                }
                sx={{ borderRadius: 2 }}
              >
                <ListItemText primary={s} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* ───────── main content ───────── */}
      <Box sx={{ flexGrow: 1 }}>
        {monthOrder.map((m) => {
          const camps = filteredGrouped[m];
          const totals = camps.reduce(
            (acc, x) => ({
              revenue: acc.revenue + (x.campaign_sum ?? 0),
              cost: acc.cost + (x.campaign_cost ?? 0),
            }),
            { revenue: 0, cost: 0 },
          );
          const profit = totals.revenue - totals.cost;
          const earnings = profit * 0.2;

          return (
            <Box key={m} ref={panelRefs.current[m]} sx={{ mb: 3 }}>
              <Accordion defaultExpanded sx={{ borderRadius: 3 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {m} · {camps.length} campaigns
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <StatBadge label="Revenue" value={totals.revenue} />
                      <StatBadge label="Cost" value={totals.cost} />
                      <StatBadge label="Profit" value={profit} />
                      <StatBadge label="Earnings" value={earnings} />
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={3}>
                    {camps.map((c) => {
                      const rev = c.campaign_sum ?? 0;
                      const cost = c.campaign_cost ?? 0;
                      const campProfit = rev - cost;
                      const campEarnings = campProfit * 0.2;

                      return (
                        <Grid key={c.campaign_id} item xs={12} sm={6} md={4} lg={3}>
                          <Paper
                            onClick={() => navigate(`/editcampaign/${c.campaign_id}`)}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                              borderRadius: 3,
                            }}
                          >
                            {renderIcon('campaigns')}
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              {c.campaign_name}
                            </Typography>

                            <StatRow label="Status" value={c.status} />
                            <StatRow label="Revenue" value={rev} format />
                            <StatRow label="Cost" value={cost} format />
                            <StatRow label="Profit" value={campProfit} format color="primary.main" />
                            <StatRow
                              label="Your Earnings"
                              value={campEarnings}
                              format
                              color="success.main"
                            />

                            <Divider sx={{ my: 1 }} />
                            <StatRow
                              label="Invoice Date"
                              value={
                                c.invoice_date
                                  ? new Date(c.invoice_date).toLocaleDateString()
                                  : 'No invoice date'
                              }
                            />
                            <StatRow
                              label="Paid Date"
                              value={
                                c.paid_date
                                  ? new Date(c.paid_date).toLocaleDateString()
                                  : 'No payment date'
                              }
                            />

                            <Divider sx={{ my: 1 }} />
                            {c.client_mappings && c.client_mappings.length > 0 ? (
                              c.client_mappings.map((cm) => (
                                <Box key={cm.id} sx={{ mb: 1 }}>
                                  <Typography variant="body2">
                                    <strong>Client:</strong> {cm.client_full_name}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Company:</strong> {cm.client_company}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Email:</strong> {cm.client_email}
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No client assigned
                              </Typography>
                            )}

                            <Divider sx={{ my: 1 }} />
                            {/* Payment & Invoice Statuses */}
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>
                              Payment Mappings
                            </Typography>
                            <List disablePadding>
                              {c.payouts_to_invoices && c.payouts_to_invoices.length > 0 ? (
                                c.payouts_to_invoices.map((mpi, idx) => (
                                  <React.Fragment key={idx}>
                                    <ListItem
                                      disableGutters
                                      sx={{
                                        py: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                      }}
                                    >
                                      <ListItemText
                                        primary={
                                          <Typography variant="body2">
                                            <strong>PO:</strong> {mpi.po_number || 'N/A'}
                                          </Typography>
                                        }
                                      />
                                      <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="body2">
                                          <strong>Invoice Status:</strong>{' '}
                                          {mpi.invoice ? mpi.invoice.status : 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Payout Status:</strong> {mpi.payout.status}
                                        </Typography>
                                      </Box>
                                    </ListItem>
                                    {idx < c.payouts_to_invoices.length - 1 && <Divider />}
                                  </React.Fragment>
                                ))
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  No payouts or invoices mapped
                                </Typography>
                              )}
                            </List>

                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {c.paid_date ? 'Eligible for payout' : 'Not yet eligible for payout'}
                            </Typography>

                            <Box sx={{ mt: 2 }}>
                              {c.paid_date ? (
                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log(`Requesting payout of ${campEarnings}`);
                                  }}
                                >
                                  Request Payout of {fmt(campEarnings)}
                                </Button>
                              ) : (
                                <Button variant="outlined" fullWidth disabled>
                                  Campaign hasn't been paid yet
                                </Button>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CampaignsView;
